/**
 * SugestoesReprocessamento (ADR-036, Req 4, design §5) — o cartão "Sugestões" do painel, só para o administrador:
 * quantas visitas foram lidas com um prompt antigo (com o custo de ler de novo) e quantas foram conferidas antes da
 * mudança de parâmetros ou bases. Mostra só as que têm visitas e some quando não há nenhuma. Cada sugestão só ABRE o
 * diálogo de reprocessamento com a pendência — nada é disparado sozinho.
 */

import { useState } from 'react';
import { useAuth, hasAnyProfile } from '../contexts/AuthContext';
import { useSugestoesReprocessamento } from '../hooks/usePainel';
import type { PendenciaVisita } from '../services/consulta-types';
import { formatarUsd } from '../pages/LotesPage';
import { ReprocessarDialog } from './ReprocessarDialog';

const numero = new Intl.NumberFormat('pt-BR');

export function SugestoesReprocessamento(): JSX.Element | null {
  const podeReprocessar = hasAnyProfile(useAuth(), ['admin']);
  const { sugestoes } = useSugestoesReprocessamento(podeReprocessar);
  const [aberta, setAberta] = useState<PendenciaVisita | null>(null);

  if (!podeReprocessar || !sugestoes) return null;
  const { promptAntigo, regrasAntigas } = sugestoes;
  if (promptAntigo.visitas === 0 && regrasAntigas.visitas === 0) return null;

  const custo =
    promptAntigo.custoEstimadoUsd === null
      ? 'sem estimativa de custo'
      : `custo estimado ${formatarUsd(promptAntigo.custoEstimadoUsd)}`;
  const um = (n: number, singular: string, plural: string): string => `${numero.format(n)} ${n === 1 ? singular : plural}`;

  return (
    <section className="card" data-testid="sugestoes-reprocessamento">
      <h2>Sugestões</h2>
      <ul className="lista-simples">
        {promptAntigo.visitas > 0 && (
          <li>
            {`${um(promptAntigo.visitas, 'visita foi lida', 'visitas foram lidas')} com um prompt antigo — ler a imagem de novo (${custo})`}{' '}
            <button type="button" className="btn btn-ghost" onClick={() => setAberta('prompt_antigo')}>
              Ler a imagem de novo
            </button>
          </li>
        )}
        {regrasAntigas.visitas > 0 && (
          <li>
            {`${um(regrasAntigas.visitas, 'visita foi conferida', 'visitas foram conferidas')} antes da mudança de parâmetros ou bases — conferir de novo`}{' '}
            <button type="button" className="btn btn-ghost" onClick={() => setAberta('regras_antigas')}>
              Conferir de novo
            </button>
          </li>
        )}
      </ul>
      {aberta && (
        <ReprocessarDialog alvo={{ tipo: 'pendencia', pendencia: aberta }} onFechar={() => setAberta(null)} />
      )}
    </section>
  );
}
